/* @flow */

import { DateTime } from 'luxon';
import {
  getConversationId,
  getConversationMessages,
  getConversationUnreadNotificationsCount,
} from '@e180/domain/lib/conversation/util';

import * as braindateTypes from 'src/shared/domain/braindate/action/braindateActionTypes';
import * as types from 'src/shared/domain/conversation/action/conversationActionTypes';
import * as entititesTypes from 'src/shared/domain/base/action/entityActionTypes';
import { sortMessages } from 'src/shared/domain/conversation/util/messageUtils';
import { createReducer } from 'src/shared/core/util/reducerUtils';

const initialState = {};

export const _addMessage = (state: Object, action: Object) => {
  const { conversationId, message } = action;
  const { conversation_step: step } = message;
  const conversation = state[conversationId];
  const messages = getConversationMessages(conversation) || [];
  const newMessages = [...messages, message];

  return {
    ...state,
    [`${conversationId}`]: {
      ...conversation,
      messages: sortMessages(newMessages),
      step,
    },
  };
};

const _updateActions = (state: Object, action: Object) => {
  const { actions, conversationId } = action;
  const conversation = state[conversationId];

  return {
    ...state,
    [`${conversationId}`]: {
      ...conversation,
      actions,
    },
  };
};

const _updateUnreadCount = (state: Object, action: Object) => {
  const { conversationId, count } = action;
  const conversation = state[conversationId];

  return {
    ...state,
    [`${conversationId}`]: {
      ...conversation,
      unread_count: count,
    },
  };
};

const _markAsRead = (state: Object, action: Object) => {
  const { conversationId } = action;
  const conversation = state[conversationId];

  return {
    ...state,
    [`${conversationId}`]: {
      ...conversation,
      unread_count: 0,
      last_read: DateTime.local().toISO(),
    },
  };
};

const _incrementUnreadMessage = (state: Object, action: Object) => {
  const { conversation } = action;
  const count = getConversationUnreadNotificationsCount(conversation);

  return {
    ...state,
    [`${getConversationId(conversation)}`]: {
      ...conversation,
      unread_count: count + 1,
    },
  };
};

const _patch = (state: Object, action: Object) => {
  const { entities } = action;
  const { conversations } = entities;

  if (conversations && typeof conversations === 'object') {
    const result = {};

    Object.keys(conversations).forEach((id) => {
      const oldConvo = state[id] || {};
      const newConvo = conversations[id];

      const mergedConvo = {
        ...oldConvo,
        ...newConvo,
      };

      const oldMessages = getConversationMessages(oldConvo) || [];
      const newMessages = getConversationMessages(newConvo) || [];

      mergedConvo.messages = sortMessages([...oldMessages, ...newMessages]);

      result[id] = mergedConvo;
    });

    return {
      ...state,
      ...result,
    };
  }
  return state;
};

const _removeConversationFromTopic = (state: Object, action: Object) => {
  const { conversationId } = action;
  const conversation = state[conversationId];

  return {
    ...state,
    [`${conversationId}`]: {
      ...conversation,
      topic: undefined,
    },
  };
};

export default createReducer(initialState, {
  [types.RECEIVE_MESSAGE]: _addMessage,
  [types.MARK_CONVERSATION_AS_READ_SUCCESS]: _markAsRead,
  [types.INCREMENT_CONVERSATION_UNREAD_MESSAGE]: _incrementUnreadMessage,
  [types.FETCH_CONVERSATION_ACTIONS_SUCCESS]: _updateActions,
  [types.REMOVE_CONVERSATION_FROM_TOPIC]: _removeConversationFromTopic,
  [braindateTypes.UPDATE_CONVERSATION_UNREAD_COUNT]: _updateUnreadCount,
  [entititesTypes.PATCH_ENTITIES]: _patch,
});
